<template>
  <scoped>
    <table>
      <thead>
        <tr>
          <th v-if="!readOnly">
            <Button
              title="Add"
              @click="add()"
            />
          </th>
          <th>
            Type
          </th>
          <th>
            Name
          </th>
          <th>
            Location
          </th>
          <th>
            Phone
          </th>
          <th>
            Email
          </th>
        </tr>
      </thead>
      <tbody v-if="items && items.length > 0">
        <template
          v-for="(contactDetails, index) in items"
          :key="contactDetails.id"
        >
          <tr
            v-if="!contactDetails.deletedAt"
            :draggable="!readOnly"
            @click="select(index)"
            @dragstart="dragStart($event, index)"
            @drop="drop($event, index)"
            @dragenter.prevent
            @dragover.prevent
          >
            <td v-if="!readOnly">
              <Button
                title="Remove"
                @click="remove($event, index)"
              />
            </td>
            <td>
              {{ contactDetails.type }}
            </td>
            <td>
              {{ contactDetails.forename }}
              {{ contactDetails.surnamePrefix }}
              {{ contactDetails.surname }}
            </td>
            <td>
              {{ contactDetails.address }}
              {{ contactDetails.postalCode }}
              {{ contactDetails.city }}
              {{ contactDetails.country }}
            </td>
            <td>
              {{ contactDetails.phone }}
            </td>
            <td>
              {{ contactDetails.email }}
            </td>
          </tr>
        </template>
      </tbody>
    </table>
  </scoped>
</template>

<script setup lang="ts">
  import Button from "@/components/button.vue"
  import {ContactDetails} from "@/endpoints/rutsolve/gql/generated/graphql"
  import {nextTick} from "vue"

  defineProps({
    readOnly: {
      default: false,
      required: false,
      type: Boolean
    }
  })

  const emits = defineEmits(["select"])

  const items = defineModel("items", {
    default: null,
    required: true,
    type: Array<ContactDetails>
  })
  
  const selectedIndex = defineModel("selectedIndex", {
    default: null,
    required: false,
    type: Number
  })

  async function select(index: number) {
    selectedIndex.value = index
    await nextTick()
    emits("select")
  }

  function dragStart(event: DragEvent, index: number) {
    event.dataTransfer.dropEffect = "move"
    event.dataTransfer.effectAllowed = "move"
    event.dataTransfer.setData("INDEX", index.toString())
  }

  function drop(event: DragEvent, index: number) {
    const startIndex = parseInt(event.dataTransfer.getData("INDEX"))
    items.value.splice(index, 0, items.value.splice(startIndex, 1)[0])
    updatePriority()
  }

  function updatePriority() {
    for (const [index, contactDetails] of items.value.entries()) {
      contactDetails.priority = index
    }
  }

  function remove(event: MouseEvent, index: number) {
    event.stopPropagation()
    items.value[index].deletedAt = new Date().toISOString()
  }

  function add() {
    items.value.push({})
    updatePriority()
    select(items.value.length - 1)
  }
</script>

<style scoped>
  table {
    > tbody {
      > tr {
        &:hover {
          background-color: var(--theme-color-secondary);
          color: var(--color-white);
          cursor: pointer;
        }
      }
    }
  }
</style>
